import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuditApi, type AuditEntry } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const RESOURCE_FILTERS = [
  { value: '', label: 'ทั้งหมด' },
  { value: 'pricing_config', label: 'ราคา' },
  { value: 'promo_rules', label: 'โปรโมชั่น' },
  { value: 'product_images', label: 'รูปสินค้า' },
  { value: 'brand_settings', label: 'Brand Settings' },
  { value: 'reply_templates', label: 'Templates' },
  { value: 'r2_object', label: 'Image upload' },
  { value: 'cache', label: 'Cache' },
];

export default function AuditPage() {
  const [resourceType, setResourceType] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const q = useQuery({
    queryKey: ['audit', resourceType, actionFilter],
    queryFn: () =>
      AuditApi.list({
        limit: 200,
        ...(resourceType ? { resourceType } : {}),
        ...(actionFilter ? { action: actionFilter } : {}),
      }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">resource:</span>
        {RESOURCE_FILTERS.map((f) => (
          <Button
            key={f.value}
            variant={resourceType === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setResourceType(f.value)}
          >
            {f.label}
          </Button>
        ))}
        <Input
          placeholder="action filter (e.g., promo.update)"
          className="ml-auto w-64"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
        />
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2">เวลา</th>
              <th className="px-3 py-2">ผู้แก้</th>
              <th className="px-3 py-2">action</th>
              <th className="px-3 py-2">resource</th>
              <th className="px-3 py-2">id</th>
              <th className="px-3 py-2">change</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  กำลังโหลด…
                </td>
              </tr>
            )}
            {q.data?.map((e) => (
              <AuditRow key={e.id} entry={e} />
            ))}
            {q.data && q.data.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  ไม่มี audit log
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const [open, setOpen] = useState(false);
  const ts = new Date(entry.createdAt).toLocaleString('th-TH', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const actionColor = actionToColor(entry.action);

  return (
    <>
      <tr
        className="border-t border-border hover:bg-muted/30 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <td className="px-3 py-2 font-mono text-muted-foreground">{ts}</td>
        <td className="px-3 py-2">{entry.actorEmail.split('@')[0]}</td>
        <td className="px-3 py-2">
          <Badge variant="outline" className={actionColor}>
            {entry.action}
          </Badge>
        </td>
        <td className="px-3 py-2 font-mono text-muted-foreground">
          {entry.resourceType ?? '—'}
        </td>
        <td className="px-3 py-2 font-mono">{entry.resourceId ?? '—'}</td>
        <td className="px-3 py-2 text-muted-foreground">
          {entry.beforeValue || entry.afterValue ? '👁 view' : '—'}
        </td>
      </tr>
      {open && (entry.beforeValue || entry.afterValue) && (
        <tr className="bg-muted/10">
          <td colSpan={6} className="px-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">before</div>
                <pre className="text-xs font-mono bg-card border border-border rounded p-2 overflow-auto max-h-60">
                  {JSON.stringify(entry.beforeValue ?? null, null, 2)}
                </pre>
              </div>
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">after</div>
                <pre className="text-xs font-mono bg-card border border-border rounded p-2 overflow-auto max-h-60">
                  {JSON.stringify(entry.afterValue ?? null, null, 2)}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function actionToColor(action: string): string {
  if (action.endsWith('.create')) return 'text-emerald-600 border-emerald-300';
  if (action.endsWith('.update')) return 'text-blue-600 border-blue-300';
  if (action.endsWith('.delete') || action.endsWith('.deactivate'))
    return 'text-red-600 border-red-300';
  if (action.startsWith('cache')) return 'text-amber-600 border-amber-300';
  if (action === 'image.upload') return 'text-indigo-600 border-indigo-300';
  return '';
}
